import { z } from "zod";

export const captionTemplatePreviewAssetRunSchema = z.object({
  id: z.string(),
  templateId: z.string(),
  workflowId: z.string(),
  status: z.enum([
    "running",
    "completed",
    "failed",
    "canceled",
    "terminated",
    "timed_out",
    "continued_as_new",
    "not_found",
    "unknown",
  ]),
  definitionVersion: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  staleReason: z.string().nullable().optional(),
});

export const captionTemplateMetadataSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(["draft", "published", "archived"]).default("published"),
  definitionVersion: z.string().default("v1"),
  renderingEngineVersion: z.string().nullable().optional(),
  categories: z.array(z.string()).default([]),
  audienceSegments: z.array(z.string()).default([]),
  rank: z.number().nullable().optional(),
  numViews: z.number().nullable().optional(),
  previewVideoUrl: z.string().nullable().optional(),
  thumbnailUrl: z.string().nullable().optional(),
  colors: z.array(z.string()).default([]),
  latestPreviewAssetRun: captionTemplatePreviewAssetRunSchema.nullable().optional(),
});

export const listCaptionTemplateMetadataParamsSchema = z.object({
  category: z.array(z.string()).optional(),
  status: z.array(z.string()).optional(),
  audienceSegment: z.array(z.string()).optional(),
  q: z.string().optional(),
  pageSize: z.coerce.number().int().positive().optional(),
  pageToken: z.string().optional(),
});

export const captionTemplateMetadataParamsSchema = z.object({
  status: z.array(z.string()).optional(),
  audienceSegment: z.array(z.string()).optional(),
});

export const listCaptionTemplateMetadataResponseSchema = z.object({
  templates: z.array(captionTemplateMetadataSchema),
  nextPageToken: z.string().nullish(),
});

export const startCaptionTemplatePreviewAssetsRequestSchema = z.object({
  overwrite: z.boolean().default(true),
});

export const startCaptionTemplatePreviewAssetsResponseSchema = z.object({
  run: captionTemplatePreviewAssetRunSchema,
});

const captionTemplateCategoryEnum = z.enum([
  "all",
  "featured",
  "viral",
  "chaotic",
  "attention",
  "active",
  "hype",
  "simple",
  "retro",
]);

export const captionTemplateCategorySchema = captionTemplateCategoryEnum.catch("all");
export const captionTemplateCategories = captionTemplateCategoryEnum.options;

const captionTemplateAudienceEnum = z.enum(["all", "production", "internal"]);

export const captionTemplateAudienceSchema = captionTemplateAudienceEnum.catch("all");
export const captionTemplateAudienceOptions = captionTemplateAudienceEnum.options;

const captionTemplateStatusFilterEnum = z.enum(["published", "draft", "archived", "all"]);

export const captionTemplateStatusFilterSchema = captionTemplateStatusFilterEnum.catch("published");
export const captionTemplateStatusFilterOptions = captionTemplateStatusFilterEnum.options;

export type CaptionTemplateMetadata = z.infer<typeof captionTemplateMetadataSchema>;
export type CaptionTemplatePreviewAssetRun = z.infer<typeof captionTemplatePreviewAssetRunSchema>;
export type CaptionTemplateCategory = z.infer<typeof captionTemplateCategoryEnum>;
export type CaptionTemplateAudience = z.infer<typeof captionTemplateAudienceEnum>;
export type CaptionTemplateStatusFilter = z.infer<typeof captionTemplateStatusFilterEnum>;
